import { IconAttachmentPDF } from '@nxsys/assets/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppIconImage from '@nxsys/components/AppIconImage';
import AppTabGroupContainer from '@nxsys/components/AppTabGroupContainer';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  StyledFormViewItem,
  StyledFormViewLabel,
  StyledFormViewValue,
} from '@nxsys/components/AppFormView/index.styled';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { getSafeRecRTIHistoryLatest } from '@nxsys/store/slices/RTI/action';
import { Icons } from '@nxsys/types/icons';
import { convertCamelToSpaces } from '@nxsys/utils';
import { Tooltip } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

type TabKey = 'request' | 'response';

type SafeRecPayslipSubmissionRecord = {
  success?: boolean;
  stage?: string;
  trigger?: 'AUTO' | 'MANUAL';
  errorMsg?: string | null;
  errorRaw?: { status?: number } | null;
  url?: string | null;
  integratedCompanyId?: string | null;
  // `request` holds the params submitted to SafeRec; `payslip` is the S3 url
  // of the submitted PDF (null when it was regenerated).
  responseData?: {
    request?: Record<string, string | null>;
    response?: any;
  } | null;
  submittedBy?: { email?: string; forename?: string; surname?: string } | null;
  createdAt?: string;
};

// Request params SafeRec v2 PostPayslip marks as required.
const REQUIRED_REQUEST_FIELDS = new Set<string>([
  'payslip',
  'email',
  'firstname',
  'lastname',
  'birthdate',
  'niCategoryLetter',
]);

const formatText = (value: any): string => {
  // SafeRec v2 takes the literal string 'null' for an unknown NI letter.
  if (value === 'null') return 'null';
  if (value == null || value === '') return '_';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const SafeRecPayslipSubmissionDetails: React.FC = () => {
  const { payrollId } = useParams<{ payrollId: string }>();
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { goBack } = useGoBackByQuery();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SafeRecPayslipSubmissionRecord | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('request');

  useEffect(() => {
    if (!payrollId) return;
    setLoading(true);
    setData(null);
    dispatch(
      getSafeRecRTIHistoryLatest({ type: 'PAYSLIP', submissionId: payrollId }),
    )
      .unwrap()
      .then((resp: any) => setData(resp?.data ?? null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [payrollId]);

  const renderStatus = (success?: boolean) => (
    <AppTagStatus className={`tag-popup ${success ? 'success' : 'failed'}`}>
      {success
        ? messages['common.success'] || 'Success'
        : messages['common.failed'] || 'Failed'}
    </AppTagStatus>
  );

  const renderRow = (
    key: string,
    content: React.ReactNode,
    required?: boolean,
  ) => (
    <StyledFormViewItem layout="horizontal" key={key}>
      <StyledFormViewLabel layout="horizontal">
        {convertCamelToSpaces(key)}
        {required && (
          <>
            {' '}
            <span className="text-red-500">*</span>
          </>
        )}
      </StyledFormViewLabel>
      <StyledFormViewValue layout="horizontal">{content}</StyledFormViewValue>
    </StyledFormViewItem>
  );

  const renderCard = (title: string, rows: React.ReactNode[]) => (
    <div className="flex flex-col gap-4 pb-4">
      <div className="bg-white rounded-md p-4">
        <div className="mb-4 flex items-center border-b border-neutral-100 pb-4">
          <div className="mr-2 border border-neutral-200 rounded-md p-2 w-9 h-9 flex items-center justify-center">
            <Icons.IconDocs color="#3E454D" />
          </div>
          <div className="text-neutral-700 text-base font-medium">{title}</div>
        </div>
        {rows.length === 0 ? (
          <div className="text-neutral-500 py-2">
            {messages['common.noData'] || 'No data'}
          </div>
        ) : (
          rows
        )}
      </div>
    </div>
  );

  const requestRows = useMemo(() => {
    if (!data) return [] as React.ReactNode[];
    const request = data.responseData?.request ?? {};
    return [
      ...Object.entries(request).map(([key, value]) => {
        const required = REQUIRED_REQUEST_FIELDS.has(key);
        if (key !== 'payslip')
          return renderRow(key, formatText(value), required);
        return renderRow(
          key,
          value ? (
            <Tooltip title={messages['common.viewFile'] || 'View file'}>
              <span
                className="cursor-pointer"
                onClick={() =>
                  window.open(value, '_blank', 'noopener,noreferrer')
                }
              >
                <AppIconImage src={IconAttachmentPDF} alt="pdf" />
              </span>
            </Tooltip>
          ) : (
            messages['common.payslipRegenerated'] ||
              'PDF was regenerated (no S3 file)'
          ),
          required,
        );
      }),
      renderRow('url', formatText(data.url)),
      // The SafeRec companyId path param, also required.
      renderRow(
        'integratedCompanyId',
        formatText(data.integratedCompanyId),
        true,
      ),
    ];
  }, [data, messages]);

  const responseRows = useMemo(() => {
    if (!data) return [] as React.ReactNode[];
    const submittedBy = data.submittedBy
      ? `${data.submittedBy.forename ?? ''} ${data.submittedBy.surname ?? ''}`.trim() ||
        data.submittedBy.email
      : null;
    return [
      renderRow('success', renderStatus(data.success)),
      renderRow('stage', formatText(data.stage)),
      renderRow('trigger', formatText(data.trigger)),
      renderRow('submittedBy', formatText(submittedBy)),
      renderRow(
        'createdAt',
        data.createdAt
          ? moment(data.createdAt).format('DD/MM/YYYY HH:mm')
          : '_',
      ),
      renderRow('errorMsg', formatText(data.errorMsg)),
      renderRow('httpStatus', formatText(data.errorRaw?.status)),
      renderRow(
        'response',
        data.responseData?.response != null ? (
          <pre className="whitespace-pre-wrap break-all m-0">
            {JSON.stringify(data.responseData.response, null, 2)}
          </pre>
        ) : (
          '_'
        ),
      ),
    ];
  }, [data, messages]);

  const items = [
    { label: messages['common.viewRequest'] || 'View Request', key: 'request' },
    {
      label: messages['common.viewResponse'] || 'View Response',
      key: 'response',
    },
  ];

  const renderTitle = useCallback(
    () => (
      <div className="flex flex-row gap-4 items-center">
        <AppBackButton
          onClick={() =>
            goBack({
              fromPathQueryKey: 'from',
              fallbackPathDefault: '/reports/payroll/payment-summary',
            })
          }
        />
        <div className="text-neutral-700">
          {messages['common.safeRecPayslipSubmission'] ||
            'SafeRec Payslip Submission'}
        </div>
      </div>
    ),
    [messages, goBack],
  );

  return (
    <AppsContainer
      title={renderTitle()}
      type="bottom"
      fullView
      loading={loading}
    >
      <AppTabGroupContainer
        items={items}
        defaultActiveTab={activeTab}
        handleTabGroupChange={(key: TabKey) => setActiveTab(key)}
      />
      {activeTab === 'request'
        ? renderCard(messages['common.request'] || 'Request', requestRows)
        : renderCard(
            messages['common.submissionSummary'] || 'Submission Summary',
            responseRows,
          )}
    </AppsContainer>
  );
};

export default SafeRecPayslipSubmissionDetails;
